import type { FactoryEdit, FactoryForm } from '@indutrust/factory-model';
import { createFactoryFormValue } from './factory-form.util';
import {
  getFactorySectionStatus,
  type FactorySectionDeps,
} from './factory-section-status.util';

/** Target completion percentage before submitting for admin review. */
export const FACTORY_PROFILE_COMPLETION_TARGET = 80;

/** Result of {@link computeFactoryProfileCompletion}. */
export type FactoryProfileCompletionResult = {
  /** Rounded score from 0 to 100. */
  percent: number;
  /** Whether {@link percent} meets {@link FACTORY_PROFILE_COMPLETION_TARGET}. */
  meetsTarget: boolean;
  /** Whether all hard-required fields for submit-for-approval are satisfied. */
  requiredComplete: boolean;
};

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function scoreName(value: FactoryForm, deps: FactorySectionDeps): number {
  return getFactorySectionStatus('general', value, deps) === 'invalid' ? 0 : 10;
}

function scoreLocation(value: FactoryForm, deps: FactorySectionDeps): number {
  return getFactorySectionStatus('location', value, deps) === 'complete'
    ? 15
    : 0;
}

function scoreCapabilities(
  value: FactoryForm,
  deps: FactorySectionDeps
): number {
  const status = getFactorySectionStatus('capabilities', value, deps);
  if (status === 'invalid') {
    return 0;
  }
  if (status === 'incomplete') {
    return 10;
  }
  return 15;
}

function scoreContacts(value: FactoryForm, deps: FactorySectionDeps): number {
  const status = getFactorySectionStatus('contacts', value, deps);
  if (status === 'invalid') {
    return 0;
  }
  if (status === 'incomplete') {
    return 10;
  }
  return 15;
}

/**
 * Computes a weighted profile completion score (0–100) from the factory
 * edit form and side-collection counts.
 *
 * Weights mirror `libs/documentation/content/en/supplier-profile-management.md`
 * and `libs/factory/SPEC.md` Q5/Q9. Section validity helpers keep the score
 * aligned with the editor side-nav indicators.
 *
 * @example
 * const result = computeFactoryProfileCompletion(formModel(), {
 *   documentsCount: docs.length,
 *   imagesCount: images.length,
 *   machineParkCount: machines.length,
 *   projectReferencesCount: refs.length,
 *   virtualTourNodesCount: nodes.length,
 * });
 */
export function computeFactoryProfileCompletion(
  value: FactoryForm,
  deps: FactorySectionDeps
): FactoryProfileCompletionResult {
  let points = 0;

  points += scoreName(value, deps);
  points += scoreLocation(value, deps);
  points += scoreCapabilities(value, deps);
  points += scoreContacts(value, deps);

  if (isNonEmptyString(value.description)) {
    points += 10;
  }
  if (deps.documentsCount > 0) {
    points += 10;
  }
  if (deps.projectReferencesCount > 0) {
    points += 10;
  }
  if (isNonEmptyString(value.logo) || deps.imagesCount > 0) {
    points += 5;
  }

  const businessComplete =
    getFactorySectionStatus('business', value, deps) === 'complete';
  if (deps.machineParkCount > 0 || businessComplete) {
    points += 5;
  }

  if (deps.virtualTourNodesCount > 0) {
    points += 5;
  }

  const percent = Math.min(100, Math.round(points));
  const requiredComplete =
    getFactorySectionStatus('general', value, deps) !== 'invalid' &&
    getFactorySectionStatus('location', value, deps) === 'complete' &&
    getFactorySectionStatus('capabilities', value, deps) !== 'invalid' &&
    getFactorySectionStatus('contacts', value, deps) !== 'invalid';

  return {
    percent,
    meetsTarget: percent >= FACTORY_PROFILE_COMPLETION_TARGET,
    requiredComplete,
  };
}

/**
 * Computes profile completion from a loaded {@link FactoryEdit} entity.
 * Useful for MSW roster denormalization where side collections may be omitted.
 *
 * @param factory The factory entity returned by `GET /factories/:id`.
 * @param deps Optional overrides for side-collection counts not on the entity.
 */
export function computeFactoryProfileCompletionFromFactoryEdit(
  factory: FactoryEdit,
  deps?: Partial<FactorySectionDeps>
): FactoryProfileCompletionResult {
  const form = createFactoryFormValue(factory);
  const fullDeps: FactorySectionDeps = {
    documentsCount: deps?.documentsCount ?? 0,
    imagesCount: deps?.imagesCount ?? factory.images.length,
    machineParkCount: deps?.machineParkCount ?? 0,
    projectReferencesCount:
      deps?.projectReferencesCount ?? factory.project_references.length,
    virtualTourNodesCount: deps?.virtualTourNodesCount ?? 0,
  };

  return computeFactoryProfileCompletion(form, fullDeps);
}
